import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sortStudentsByName } from '@macmillan-learning/gradebook-shared';
import MLIcon from '../../../../MLIcon/MLIcon';
import { useSearchParams } from '../../hooks/useSearchParams';
import { useFullRoster } from '../../../../../hooks/useFullRoster';
import { normalizeString } from '../../../../../utils/InstructorGradebookHelpers';

import s from './StudentSelector.module.css';

export const StudentSelector = ({
  courseId,
  currentStudent,
  updateSelectedStudent,
  setDropdownOpen,
}) => {
  const roster = useFullRoster(courseId);
  const [params, setParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const componentRef = useRef(null);
  const inputRef = useRef(null);

  const selectedStudentId = params?.get('studentId');
  const selectedStudent = roster?.find(
    ({ studentId }) => studentId === selectedStudentId,
  );
  // The full roster is fetched independently of the current student's data,
  // so fall back to the already-loaded currentStudent while it's in flight.
  const displayStudent = selectedStudent ?? currentStudent;

  const sortedStudents = useMemo(() => sortStudentsByName(roster), [roster]);

  const filteredRoster =
    displayStudent?.name === searchTerm
      ? sortedStudents
      : sortedStudents.filter(({ name }) =>
          normalizeString(name)
            .toLowerCase()
            .includes(normalizeString(searchTerm?.toLowerCase())),
        );

  const selectStudent = (student) => {
    setSearchTerm('');
    setParams({ studentId: student.studentId });
    updateSelectedStudent(student);
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        setSearchTerm(displayStudent?.name ?? '');
        break;
      case 'ArrowDown':
        if (selectedIndex < filteredRoster.length - 1) {
          setSelectedIndex((prevIndex) => prevIndex + 1);
        }
        break;
      case 'ArrowUp':
        if (selectedIndex > 0) {
          setSelectedIndex((prevIndex) => prevIndex - 1);
        }
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          selectStudent(filteredRoster[selectedIndex]);
          setSelectedIndex(-1);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const onOutsideClick = (event) => {
      if (
        componentRef?.current &&
        !componentRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutsideClick);

    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setDropdownOpen(true);
    }
  }, [isOpen]);

  return (
    <div
      className={s.dropdown}
      ref={componentRef}
      data-test-id="isr-student-search-dropdown"
    >
      {isOpen ? (
        <div className={s.openWrapper}>
          <div className={s.inputWrapper}>
            <input
              ref={inputRef}
              type="search"
              onChange={(e) => setSearchTerm(e.target?.value)}
              value={searchTerm}
              onSelect={(e) => e.currentTarget.focus()}
              onKeyDown={handleKeyDown}
              aria-label="Search students"
              placeholder={'Search students'}
              className={s.studentSearchInput}
            />
            {searchTerm && (
              <div className={s.inputIcons}>
                <button
                  className={s.clearSearchButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <MLIcon type="x" className={s.clearSearch} />
                </button>
                <MLIcon type="caret_down" />
              </div>
            )}
          </div>
          <ul
            tabIndex={-1}
            role="listbox"
            aria-labelledby="dropdown-label"
            className={s.dropdownMenu}
          >
            {!filteredRoster?.length && (
              <li className={s.noStudents}>No results</li>
            )}
            {filteredRoster?.map((student, index) => (
              <li
                key={student.studentId}
                role="option"
                aria-selected={selectedIndex === index}
                className={`${selectedIndex === index ? s.selectedItem : ''} ${
                  s.listItem
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  selectStudent(student);
                  setSelectedIndex(-1);
                  setIsOpen(false);
                  setDropdownOpen(false);
                }}
                tabIndex={0}
                onMouseOver={() => setSelectedIndex(index)}
              >
                {student.name}
                {student.studentId === selectedStudentId && (
                  <MLIcon type="check" className={s.checkIcon} />
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <h2 className={s.noMargin}>
          <button
            onClick={() => {
              setSearchTerm(displayStudent?.name ?? '');
              setIsOpen(true);
              setDropdownOpen(true);
            }}
            onKeyDown={handleKeyDown}
            className={s.dropdownToggle}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            tabIndex={0}
            aria-label="Select student"
            data-test-id="isg-student-search-dropdown"
          >
            <span className={s.firstName}>
              {displayStudent?.first_name || 'N/A'}{' '}
            </span>
            <span className={s.lastName}>{displayStudent?.last_name}</span>
            <MLIcon
              type="caret_down"
              aria-hidden="true"
              className={s.caretDown}
            />
          </button>
        </h2>
      )}
    </div>
  );
};
